import { useState, useEffect } from 'react';
import Panel from '../Panel';
import { INote } from '~/types/campaigns';
import clip from 'text-clipper';
import { safeParseHtml } from '~/utils/safeParseHtml';
import { isCampaignRequestNote } from '~/utils/notes';
import NotesModal from '~/components/Common/NotesModal';
import EditNoteModal from '~/components/Common/EditNoteModal';
import ActivityLog from '~/components/Common/ActivityLog';
import { formatDateIfValid } from '~/utils';
import { ContactAttempt } from '~/services/contactAttempts';
import { useContactAttempts } from '~/hooks/useContactAttempts';
import ContactAttemptsLoadError from '~/components/Common/ContactAttemptsLoadError';
import {
    fetchCampaignActivityLog,
    fetchCampaignRequestActivityLog,
    updateCampaignRequestNote,
    deleteCampaignRequestNote,
    fetchCampaignRequestNoteEditHistory,
} from '~/services/campaign';
import AddNoteInlineForm, {
    ADD_MSG_TYPE,
} from '~/components/CampaignDetail/forms/AddNoteInlineForm';
import AddContactAttemptInlineForm from './forms/AddContactAttemptInlineForm';
import { FaPlus, FaUser } from 'react-icons/fa6';
import { FiEye, FiEdit2 } from 'react-icons/fi';
import Check from '~/components/Check/Check';
import EditContactAttemptModal from '~/components/Common/EditContactAttemptModal';
import EditRequestNotesModal from '~/components/Common/EditRequestNotesModal';

interface ActivitiesTabsProps {
    notes: INote[];
    campaign: {
        id?: number;
        campaign_id?: number;
        name?: string;
        campaign_name?: string;
    };
    setNotes: React.Dispatch<React.SetStateAction<INote[]>>;
    campaignRequest?: {
        id: number;
        initial_first_name?: string;
        initial_last_name?: string;
        patient_first_name?: string;
        patient_middle_name?: string;
        patient_last_name?: string;
        created_date?: string;
    };
    campaignRequestNotes?: string;
    onRequestNotesUpdate?: (updatedNotes: string) => void;
    activityLogRefreshKey?: number;
}

type TabType = 'notes' | 'activity';

const ActivitiesTabs = ({
    notes,
    campaign,
    setNotes,
    campaignRequest,
    campaignRequestNotes,
    onRequestNotesUpdate,
    activityLogRefreshKey = 0,
}: ActivitiesTabsProps) => {
    const {
        contactAttempts,
        setContactAttempts,
        loadingAttempts,
        attemptsFailed,
        loadContactAttempts,
    } = useContactAttempts(campaignRequest?.id);
    const [notesModal, setNotesModal] = useState<{
        isOpen: boolean;
        note: INote | null;
    }>({ isOpen: false, note: null });
    const [editModal, setEditModal] = useState<{
        isOpen: boolean;
        note: INote | null;
    }>({ isOpen: false, note: null });
    const [editAttemptModal, setEditAttemptModal] = useState<{
        isOpen: boolean;
        attempt: ContactAttempt | null;
    }>({ isOpen: false, attempt: null });
    const [editRequestNotesModal, setEditRequestNotesModal] = useState(false);
    const [activeForm, setActiveForm] = useState<'note' | 'attempt' | null>(
        null
    );
    const [activeTab, setActiveTab] = useState<TabType>('notes');
    const [activityLogCount, setActivityLogCount] = useState<number>(0);

    // For campaign requests, show ALL notes (including todos) since there's no separate todos tab
    const justNotes = notes || [];

    // Combine campaign request notes (string) with message notes (array)
    // Show count of both types if they exist
    const hasRequestNotes =
        campaignRequestNotes && campaignRequestNotes.trim().length > 0;
    const totalNotesCount = justNotes.length + (hasRequestNotes ? 1 : 0);
    const totalHistoryCount = contactAttempts.length + totalNotesCount;

    // Get IDs for activity log
    const campaignId = campaign?.id || campaign?.campaign_id;
    const campaignRequestId = campaignRequest?.id;

    // Fetch activity log count (re-fetch when refreshKey changes)
    // Use campaign request endpoint if no campaign ID, otherwise use campaign endpoint
    useEffect(() => {
        const loadActivityLogCount = async () => {
            try {
                let response;
                if (campaignId) {
                    response = await fetchCampaignActivityLog(
                        campaignId,
                        'all',
                        1,
                        1,
                        true
                    );
                } else if (campaignRequestId) {
                    response = await fetchCampaignRequestActivityLog(
                        campaignRequestId,
                        1,
                        1
                    );
                } else {
                    return;
                }
                setActivityLogCount(response.count || 0);
            } catch (error) {
                console.error('Error fetching activity log count:', error);
            }
        };

        loadActivityLogCount();
    }, [campaignId, campaignRequestId, activityLogRefreshKey]);

    // Get client name from campaign request data
    const clientName = campaignRequest
        ? [
              campaignRequest?.patient_first_name,
              campaignRequest?.patient_middle_name,
              campaignRequest?.patient_last_name,
          ]
              .filter((e) => e)
              .join(' ') || 'Client'
        : 'Client';

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
        });
    };

    // Show request notes at the top if they exist
    const initialContactName =
        campaignRequest?.initial_first_name &&
        campaignRequest?.initial_last_name
            ? `${campaignRequest.initial_first_name} ${campaignRequest.initial_last_name}`
            : 'System';

    // Determine if the selected modal note is a CampaignRequestNote
    const selectedNoteIsCRNote = notesModal.note
        ? isCampaignRequestNote(notesModal.note)
        : false;
    const editNoteIsCRNote = editModal.note
        ? isCampaignRequestNote(editModal.note)
        : false;

    // Create a unified history list
    type HistoryItem = {
        id: string;
        date: Date;
        type: 'contact_attempt' | 'note' | 'request_note';
        data:
            | ContactAttempt
            | INote
            | { note: string; created_by: string; created_date: string };
    };

    const unifiedHistory: HistoryItem[] = [];

    // Add contact attempts
    contactAttempts.forEach((attempt) => {
        unifiedHistory.push({
            id: `contact-${attempt.id}`,
            date: new Date(attempt.attempt_datetime),
            type: 'contact_attempt',
            data: attempt,
        });
    });

    // Add notes
    justNotes.forEach((note) => {
        unifiedHistory.push({
            id: `note-${note.id}`,
            date: new Date(note.created_date),
            type: 'note',
            data: note,
        });
    });

    // Add campaign request notes if they exist
    if (hasRequestNotes && campaignRequest?.created_date) {
        unifiedHistory.push({
            id: 'request-note',
            date: new Date(campaignRequest.created_date),
            type: 'request_note',
            data: {
                note: campaignRequestNotes || '',
                created_by: initialContactName,
                created_date: campaignRequest.created_date,
            },
        });
    }

    // Sort by date (newest first)
    unifiedHistory.sort((a, b) => b.date.getTime() - a.date.getTime());

    return (
        <Panel addClasses='bg-white border-stroke !p-3'>
            <div className='overflow-hidden p-1'>
                {/* Tab Navigation */}
                <div className='mb-5 flex rounded-full bg-[#FFF5E6] p-1'>
                    <button
                        onClick={() => setActiveTab('notes')}
                        className={`flex-1 rounded-full px-4 py-2 text-[14px]/[18px] font-bold transition-colors ${
                            activeTab === 'notes'
                                ? 'bg-[#FEBF3E] text-black'
                                : 'bg-transparent text-body hover:bg-[#FFE4B5]'
                        }`}
                    >
                        History{' '}
                        <span
                            className={`ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                                activeTab === 'notes'
                                    ? 'bg-black text-white'
                                    : 'bg-body text-white'
                            }`}
                        >
                            {totalHistoryCount}
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`flex-1 rounded-full px-4 py-2 text-[14px]/[18px] font-bold transition-colors ${
                            activeTab === 'activity'
                                ? 'bg-[#FEBF3E] text-black'
                                : 'bg-transparent text-body hover:bg-[#FFE4B5]'
                        }`}
                    >
                        Activity Log{' '}
                        <span
                            className={`ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                                activeTab === 'activity'
                                    ? 'bg-black text-white'
                                    : 'bg-body text-white'
                            }`}
                        >
                            {activityLogCount}
                        </span>
                    </button>
                </div>

                {/* Notes Tab Content */}
                {activeTab === 'notes' && (
                    <>
                        <div className='mb-5 flex flex-wrap items-center justify-between gap-2'>
                            <h3 className='mb-2 text-[22px]/[28px] font-bold'>
                                History ({totalHistoryCount})
                            </h3>
                            <div className='flex gap-2'>
                                <button
                                    onClick={() =>
                                        setActiveForm(
                                            activeForm === 'attempt'
                                                ? null
                                                : 'attempt'
                                        )
                                    }
                                    className='inline-flex shrink items-center justify-center gap-1 whitespace-nowrap rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[12px] py-[6px] text-[11px]/[14px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6A830] hover:bg-[#E6A830] sm:gap-2 sm:px-[20px] sm:py-[7px] sm:text-[13px]/[16px]'
                                >
                                    <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                                    Add Attempt
                                </button>
                                <button
                                    onClick={() =>
                                        setActiveForm(
                                            activeForm === 'note' ? null : 'note'
                                        )
                                    }
                                    className='hover:bg-gray-100 inline-flex shrink items-center justify-center gap-1 whitespace-nowrap rounded-full border-[2px] border-black bg-white px-[12px] py-[6px] text-[11px]/[14px] font-bold uppercase text-black transition-colors duration-500 sm:gap-2 sm:px-[20px] sm:py-[7px] sm:text-[13px]/[16px]'
                                >
                                    <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                                    Add Note
                                </button>
                            </div>
                        </div>

                        {activeForm === 'attempt' && (
                            <AddContactAttemptInlineForm
                                clientName={clientName}
                                campaignRequestId={campaignRequest?.id}
                                onAttemptAdded={loadContactAttempts}
                                onClose={() => setActiveForm(null)}
                            />
                        )}

                        {activeForm === 'note' && (
                            <AddNoteInlineForm
                                type={ADD_MSG_TYPE.ONLY_NOTE}
                                campaign={campaign}
                                campaignRequest={campaignRequest}
                                setNotes={setNotes}
                                onClose={() => setActiveForm(null)}
                            />
                        )}

                        {attemptsFailed && !loadingAttempts && (
                            <ContactAttemptsLoadError onRetry={loadContactAttempts} />
                        )}

                        {loadingAttempts ? (
                            <div className='flex items-center justify-center py-8'>
                                <div className='text-gray-500'>
                                    Loading history...
                                </div>
                            </div>
                        ) : unifiedHistory.length === 0 && !attemptsFailed ? (
                            <div className='flex items-center justify-center py-8'>
                                <div className='text-gray-500'>
                                    No history found. Add the first entry!
                                </div>
                            </div>
                        ) : (
                            <div className='mt-3 space-y-3'>
                        {unifiedHistory.map((item) => {
                            if (item.type === 'contact_attempt') {
                                const attempt = item.data as ContactAttempt;
                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='text-[12px]/[16px]'>
                                                {formatDate(
                                                    attempt.attempt_datetime
                                                )}{' '}
                                                {attempt.method_display}
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {attempt.person_name ||
                                                        'Unknown'}
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        setEditAttemptModal({
                                                            isOpen: true,
                                                            attempt,
                                                        })
                                                    }
                                                    title='Edit contact attempt'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div className='bg-[#F4F4F5] p-2 text-[14px]/[20px]'>
                                            {attempt.note || (
                                                <em className='text-gray-500'>
                                                    No note provided
                                                </em>
                                            )}
                                        </div>
                                        <div className='text-right text-[12px]/[16px] text-[#949599]'>
                                            Contact Attempt
                                        </div>
                                    </div>
                                );
                            } else if (item.type === 'request_note') {
                                const requestNote = item.data as {
                                    note: string;
                                    created_by: string;
                                    created_date: string;
                                };
                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='text-[12px]/[16px]'>
                                                Request Notes
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {requestNote.created_by}
                                                </div>
                                                <button
                                                    onClick={() => setEditRequestNotesModal(true)}
                                                    title='Edit request notes'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div className='bg-[#F4F4F5] p-2 text-[14px]/[20px]'>
                                            {requestNote.note}
                                        </div>
                                        <div className='text-right text-[12px]/[16px] text-[#949599]'>
                                            {`Submitted on ${formatDateIfValid(
                                                requestNote.created_date,
                                                'MM/dd/yy'
                                            )}`}
                                        </div>
                                    </div>
                                );
                            } else {
                                const note = item.data as INote;
                                const hasNotes = note.note.length > 0;
                                const handleNotesClick = () => {
                                    if (hasNotes) {
                                        setNotesModal({
                                            isOpen: true,
                                            note: note,
                                        });
                                    }
                                };

                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center gap-1 text-[12px]/[16px]'>
                                                {note.todo_flag && (
                                                    note.completed_date
                                                        ? <Check />
                                                        : <FaUser className='h-4 w-4' style={{ color: '#f97316' }} />
                                                )}
                                                {formatDateIfValid(
                                                    note.created_date,
                                                    'MM/dd/yy'
                                                )}
                                                {' '}- {note.todo_flag ? 'To Do' : 'Note'}
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {note.created_by_name}
                                                </div>
                                                <button onClick={handleNotesClick} title='View note'>
                                                    <FiEye className='text-primary' />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setEditModal({
                                                            isOpen: true,
                                                            note: note,
                                                        })
                                                    }
                                                    title='Edit note'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            className='cursor-pointer bg-[#F4F4F5] p-2 text-[14px]/[20px] transition-colors hover:text-primary [&_a]:text-primary [&_a]:underline'
                                            onClick={handleNotesClick}
                                            title={
                                                hasNotes
                                                    ? 'Click to view full notes'
                                                    : ''
                                            }
                                        >
                                            {safeParseHtml(
                                                clip(note.note, 140, {
                                                    html: true,
                                                    maxLines: 5,
                                                })
                                            )}
                                        </div>
                                        {(note as any).assignee && (
                                            <div className='mt-1 text-[12px]/[16px] text-[#949599]'>
                                                Assigned to: {(note as any).assignee}
                                            </div>
                                        )}
                                    </div>
                                );
                                }
                            })}
                            </div>
                        )}

                        <NotesModal
                            isOpen={notesModal.isOpen}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            note={notesModal.note as any}
                            campaignId={selectedNoteIsCRNote ? undefined : (campaignId || 0)}
                            onClose={() =>
                                setNotesModal({ isOpen: false, note: null })
                            }
                            onPinToggle={selectedNoteIsCRNote ? undefined : (updatedNote) => {
                                // Update the notes list with the updated pinned status
                                setNotes((prevNotes) =>
                                    prevNotes.map((n) =>
                                        n.id === updatedNote.id
                                            ? {
                                                  ...n,
                                                  pinned_flag:
                                                      updatedNote.pinned_flag ??
                                                      false,
                                              }
                                            : n
                                    )
                                );
                            }}
                            onDelete={(noteId: number) => {
                                setNotes((prevNotes) =>
                                    prevNotes.filter((n) => n.id !== noteId)
                                );
                            }}
                            customDeleteFn={
                                selectedNoteIsCRNote && campaignRequestId
                                    ? (noteId) => deleteCampaignRequestNote(campaignRequestId, noteId)
                                    : undefined
                            }
                            customFetchHistoryFn={
                                selectedNoteIsCRNote && campaignRequestId
                                    ? (noteId) => fetchCampaignRequestNoteEditHistory(campaignRequestId, noteId)
                                    : undefined
                            }
                        />

                        <EditNoteModal
                            isOpen={editModal.isOpen}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            note={editModal.note as any}
                            campaignId={editNoteIsCRNote ? 0 : (campaignId || 0)}
                            onClose={() => setEditModal({ isOpen: false, note: null })}
                            onSave={(updatedNote) => {
                                setNotes((prevNotes) =>
                                    prevNotes.map((n) =>
                                        n.id === updatedNote.id
                                            ? {
                                                  ...n,
                                                  note: updatedNote.note,
                                                  todo_flag: updatedNote.todo_flag ?? n.todo_flag,
                                                  completed_date: updatedNote.completed_date ?? null,
                                                  completed_by_name: updatedNote.completed_by_name || '',
                                                  assignee: updatedNote.assignee,
                                                  assignment: updatedNote.assignment ? Number(updatedNote.assignment) : null,
                                              } as INote
                                            : n
                                    )
                                );
                            }}
                            hidePinToggle={editNoteIsCRNote}
                            customUpdateFn={
                                editNoteIsCRNote && campaignRequestId
                                    ? (noteId, payload) => updateCampaignRequestNote(campaignRequestId, noteId, payload)
                                    : undefined
                            }
                            customFetchHistoryFn={
                                editNoteIsCRNote && campaignRequestId
                                    ? (noteId) => fetchCampaignRequestNoteEditHistory(campaignRequestId, noteId)
                                    : undefined
                            }
                        />

                        <EditContactAttemptModal
                            isOpen={editAttemptModal.isOpen}
                            attempt={editAttemptModal.attempt}
                            onClose={() => setEditAttemptModal({ isOpen: false, attempt: null })}
                            onSave={(updated) => {
                                setContactAttempts((prev) =>
                                    prev.map((a) => (a.id === updated.id ? updated : a))
                                );
                            }}
                        />

                        {campaignRequestId && (
                            <EditRequestNotesModal
                                isOpen={editRequestNotesModal}
                                notes={campaignRequestNotes || ''}
                                campaignRequestId={campaignRequestId}
                                onClose={() => setEditRequestNotesModal(false)}
                                onSave={(updatedNotes) => {
                                    onRequestNotesUpdate?.(updatedNotes);
                                }}
                            />
                        )}
                    </>
                )}
                {/* End of Notes Tab Content */}

                {/* Activity Log Tab Content */}
                {activeTab === 'activity' && (campaignId || campaignRequestId) && (
                    <>
                        <h3 className='mb-3 text-[16px]/[22px] font-bold'>
                            Activity Log ({activityLogCount})
                        </h3>
                        <ActivityLog
                            campaignId={campaignId}
                            campaignRequestId={campaignRequestId}
                            refreshKey={activityLogRefreshKey}
                        />
                    </>
                )}
            </div>
        </Panel>
    );
};

export default ActivitiesTabs;
